import { DataTable, MainButton, PageWrapper, generateAvatarInitialsImage } from '@repo/ui';
import { ListCourseResponse } from 'domain/models/responses';
import { GetAllEntitiesResponse } from 'domain/models/responses';
import { useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import { useService } from 'presentation/app/hooks/useService';

import { makeColumnsPartners } from '../common/columns/columns-partners';
import { ModalCreatePartner } from '../components/modal-create-partner';
import { ModalEditPartner } from '../components/modal-edit-partner';
import { ModalEditPartnerBase } from '../components/modal-edit-partner-base';
import { ModalGenerateKey } from '../components/modal-generate-key';
import { ModalShowKey } from '../components/modal-show-key';
import { Partner, PartnerRequest } from '../models';

export const Partners = () => {
  const { partnersService, entitiesService, courseService } = useService();

  const [partnersList, setPartnersList] = useState<Partner[]>([]);
  const [requestsList, setRequestsList] = useState<PartnerRequest[]>([]);
  const [entitiesList, setEntitiesList] = useState<GetAllEntitiesResponse[]>([]);
  const [coursesList, setCoursesList] = useState<ListCourseResponse[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [loadingPartners, setLoadingPartners] = useState(false);

  const [isModalCreateOpen, setIsModalCreateOpen] = useState(false);
  const [isModalEditRequestOpen, setIsModalEditRequestOpen] = useState(false);
  const [isModalEditPartnerOpen, setIsModalEditPartnerOpen] = useState(false);
  const [isModalGenerateKeyOpen, setIsModalGenerateKeyOpen] = useState(false);
  const [isModalShowKeyOpen, setIsModalShowKeyOpen] = useState(false);

  const [loadingCreate, setLoadingCreate] = useState(false);
  const [loadingEditRequest, setLoadingEditRequest] = useState(false);
  const [loadingGenerateKey, setLoadingGenerateKey] = useState(false);

  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [generatedKey, setGeneratedKey] = useState('');
  const [generatedKeyPartnerName, setGeneratedKeyPartnerName] = useState('');

  const fetchRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const data = await partnersService.getAllPartnerRequests();
      setRequestsList(data as PartnerRequest[]);
    } finally {
      setLoadingRequests(false);
    }
  }, [partnersService]);

  const fetchPartners = useCallback(async () => {
    setLoadingPartners(true);
    try {
      const data = await partnersService.getAllPartners();
      setPartnersList(data as Partner[]);
    } finally {
      setLoadingPartners(false);
    }
  }, [partnersService]);

  const fetchEntities = useCallback(async () => {
    try {
      const data = await entitiesService.getAllEntities();
      setEntitiesList(data);
    } catch {}
  }, [entitiesService]);

  const fetchCourses = useCallback(async () => {
    try {
      const data = await courseService.courseList();
      setCoursesList(data);
    } catch {}
  }, [courseService]);

  useEffect(() => {
    fetchRequests();
    fetchPartners();
    fetchEntities();
    fetchCourses();
  }, [fetchRequests, fetchPartners, fetchEntities, fetchCourses]);

  const formikCreate = useFormik({
    initialValues: {
      partner: null as { label: string; value: number } | null,
      partnerName: '',
      description: '',
      redirectUrl: '',
      active: true,
      requestedData: [] as { key: string; title: string; mandatory: boolean }[],
      organizations: [] as { label: string; value: string }[],
      courses: [] as { label: string; value: string }[],
    },
    onSubmit: async (values, helpers) => {
      if (!values.partner) return;
      setLoadingCreate(true);
      try {
        await partnersService.createPartnerRequest({
          partnerId: values.partner.value,
          description: values.partnerName,
          redirectUrl: values.redirectUrl || undefined,
          active: values.active,
          requestedData: values.requestedData,
          organizationIds: values.organizations.map((o) => o.value),
          courseIds: values.courses.map((c) => c.value),
        });
        helpers.resetForm();
        setIsModalCreateOpen(false);
        fetchRequests();
      } finally {
        setLoadingCreate(false);
      }
    },
  });

  const formikEditRequest = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: 0,
      partnerName: '',
      key: '',
      description: '',
      redirectUrl: '',
      active: true,
      organizations: [] as { label: string; value: string }[],
      courses: [] as { label: string; value: string }[],
    },
    onSubmit: async (values, helpers) => {
      setLoadingEditRequest(true);
      try {
        await partnersService.updatePartnerRequest({
          id: values.id,
          description: values.description,
          redirectUrl: values.redirectUrl || undefined,
          active: values.active,
          organizationIds: values.organizations.map((o) => o.value),
          courseIds: values.courses.map((c) => c.value),
        });
        helpers.resetForm();
        setIsModalEditRequestOpen(false);
        fetchRequests();
      } finally {
        setLoadingEditRequest(false);
      }
    },
  });

  const handleEditRequest = (row: PartnerRequest) => {
    formikEditRequest.setValues({
      id: row.id,
      partnerName: row.partner.name,
      key: row.key,
      description: row.description,
      redirectUrl: row.redirectUrl,
      active: row.active,
      organizations: (row.organizationIds ?? []).map((id) => {
        const entity = entitiesList.find((e) => e.id === id);
        return { label: entity?.name ?? id, value: id };
      }),
      courses: (row.courseIds ?? []).map((id) => {
        const course = coursesList.find((c) => c.id === id);
        return { label: course?.nome ?? id, value: id };
      }),
    });
    setIsModalEditRequestOpen(true);
  };

  const handleEditPartner = (partner: Partner) => {
    setSelectedPartner(partner);
    setIsModalEditPartnerOpen(true);
  };

  const handleGenerateKey = (partner: Partner) => {
    setSelectedPartner(partner);
    setIsModalGenerateKeyOpen(true);
  };

  const handleConfirmGenerateKey = async () => {
    if (!selectedPartner) return;
    setLoadingGenerateKey(true);
    try {
      const res = await partnersService.generatePartnerRequestKey({ id: selectedPartner.id });
      setGeneratedKey(res.apiKey);
      setGeneratedKeyPartnerName(selectedPartner.name);
      setIsModalGenerateKeyOpen(false);
      setIsModalShowKeyOpen(true);
    } finally {
      setLoadingGenerateKey(false);
    }
  };

  const handleToggleActive = async (row: PartnerRequest) => {
    await partnersService.togglePartnerRequest({ id: row.id });
    fetchRequests();
  };

  const handlePartnerCreated = (key: string, partnerName: string) => {
    setGeneratedKey(key);
    setGeneratedKeyPartnerName(partnerName);
    setIsModalShowKeyOpen(true);
    fetchPartners();
  };

  return (
    <PageWrapper title="Parceiros">
      <ModalCreatePartner
        isOpen={isModalCreateOpen}
        loading={loadingCreate || loadingPartners}
        formik={formikCreate}
        partnersList={partnersList}
        entitiesList={entitiesList}
        coursesList={coursesList}
        partnersService={partnersService}
        toggleModal={() => setIsModalCreateOpen(false)}
        resetForm={formikCreate.resetForm}
        onPartnerCreated={fetchPartners}
        onKeyGenerated={handlePartnerCreated}
      />

      <ModalEditPartner
        isOpen={isModalEditRequestOpen}
        loading={loadingEditRequest}
        formik={formikEditRequest}
        entitiesList={entitiesList}
        coursesList={coursesList}
        toggleModal={() => setIsModalEditRequestOpen(false)}
        resetForm={formikEditRequest.resetForm}
      />

      <ModalEditPartnerBase
        isOpen={isModalEditPartnerOpen}
        partner={selectedPartner}
        partnersService={partnersService}
        toggleModal={() => setIsModalEditPartnerOpen(false)}
        onUpdated={fetchPartners}
      />

      <ModalGenerateKey
        isOpen={isModalGenerateKeyOpen}
        loading={loadingGenerateKey}
        partnerName={selectedPartner?.name ?? ''}
        toggleModal={() => setIsModalGenerateKeyOpen(false)}
        onConfirm={handleConfirmGenerateKey}
      />

      <ModalShowKey
        isOpen={isModalShowKeyOpen}
        generatedKey={generatedKey}
        partnerName={generatedKeyPartnerName}
        toggleModal={() => {
          setIsModalShowKeyOpen(false);
          setGeneratedKey('');
          setGeneratedKeyPartnerName('');
        }}
      />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold">Parceiros</h4>
          <p className="text-muted mb-0">
            Solicitações de integração por parceiro.
          </p>
        </div>
        <MainButton onClick={() => setIsModalCreateOpen(true)}>
          <i className="ri-add-line" style={{ fontSize: '1rem' }} />
          Nova solicitação
        </MainButton>
      </div>

      {/* ── Parceiros ───────────────────────────────── */}
      <Card className="p-3 mb-4">
        <h6 className="fw-semibold mb-3">Parceiros</h6>
        <DataTable
          noDataTitle="Nenhum parceiro cadastrado"
          noDataDescription="Crie um parceiro ao adicionar uma nova solicitação."
          data={partnersList}
          loading={loadingPartners}
          columns={[
            {
              name: 'Parceiro',
              sortable: true,
              selector: (row: Partner) => row.name,
              cell: (row: Partner) => (
                <div className="d-flex align-items-center gap-2 py-2">
                  <img
                    src={row.logoKey || generateAvatarInitialsImage(row.name)}
                    alt={row.name}
                    className="rounded-circle object-fit-cover flex-shrink-0"
                    style={{ width: 36, height: 36 }}
                  />
                  <strong>{row.name}</strong>
                </div>
              ),
            },
          ]}
          canSearch={false}
          defaultSortAsc={true}
          defaultSortFieldId={1}
          keysSearch={['name']}
          actions={[
            {
              id: 1,
              name: 'editar-parceiro',
              label: 'Editar',
              nameFeatherIcon: 'edit',
              classNameColorIcon: 'primary',
              onPress: (row: Partner) => handleEditPartner(row),
            },
            {
              id: 2,
              name: 'gerar-api-key',
              label: 'Gerar nova chave',
              nameFeatherIcon: 'key',
              classNameColorIcon: 'warning',
              onPress: (row: Partner) => handleGenerateKey(row),
            },
          ]}
        />
      </Card>

      {/* ── Solicitações ────────────────────────────── */}
      <Card className="p-3">
        <h6 className="fw-semibold mb-3">Solicitações</h6>
        <DataTable
          noDataTitle="Nenhuma solicitação encontrada"
          noDataDescription="Não há solicitações de parceiros cadastradas no momento."
          data={requestsList}
          loading={loadingRequests}
          columns={makeColumnsPartners()}
          canSearch={true}
          defaultSortAsc={true}
          defaultSortFieldId={1}
          keysSearch={['key', 'description']}
          actions={[
            {
              id: 1,
              name: 'editar-solicitacao',
              label: 'Editar',
              nameFeatherIcon: 'edit',
              classNameColorIcon: 'primary',
              onPress: (row: PartnerRequest) => handleEditRequest(row),
            },
            {
              id: 2,
              name: 'inativar-solicitacao',
              label: 'Inativar',
              nameFeatherIcon: 'slash',
              classNameColorIcon: 'danger',
              onPress: (row: PartnerRequest) => handleToggleActive(row),
            },
          ]}
        />
      </Card>
    </PageWrapper>
  );
};
